/**
 * 사람이 견적서 화면에서 고친 노출속성 값을 지킨다(보고 3320E1 — 원문대로 고친 벨트 색이 견적 갱신 뒤 «실버»로 되돌아가 전송됨).
 * 기록 = 상품 칸 quotationUserEdits [{ optionNumber, attributeName, value }] — coupangDoc 밖이라 쿠팡에 안 간다.
 * 속성 이름에 «/»·«.» 이 들어갈 수 있어(패션의류/잡화 사이즈) 키 대신 배열로 둔다.
 * 순수 함수만 둔다(DOM·store·API 모름).
 */

const EXPOSED_VALUE_PATH = /^coupangDoc\.jsonDocument\.(\d+)\.productPage\.commonAttributes\.exposedAttributes\.(\d+)\.attributeValue$/

const same = (e, optionNumber, attributeName) => e && String(e.optionNumber) === String(optionNumber) && e.attributeName === attributeName

/**
 * 화면 저장 대기 칸(pendingChanges) 중 노출속성 값을 기록에 합친다. 같은 칸은 값만 바꾼다.
 * @param {Object} product - options[].optionNumber · coupangDoc.jsonDocument[].…exposedAttributes[].attributeName
 * @param {Object} pending - { 'coupangDoc.jsonDocument.N.productPage.commonAttributes.exposedAttributes.K.attributeValue': 값, … }
 * @returns {{ edits: Array, changed: boolean }}
 */
export function mergeUserEdits (product, pending) {
  const edits = (Array.isArray(product && product.quotationUserEdits) ? product.quotationUserEdits : []).map(e => ({ ...e }))
  let changed = false
  for (const [path, value] of Object.entries(pending || {})) {
    const m = EXPOSED_VALUE_PATH.exec(path)
    if (!m) continue
    const option = product.options && product.options[Number(m[1])]
    const doc = product.coupangDoc && product.coupangDoc.jsonDocument && product.coupangDoc.jsonDocument[Number(m[1])]
    const attr = doc && doc.productPage && doc.productPage.commonAttributes && (doc.productPage.commonAttributes.exposedAttributes || [])[Number(m[2])]
    if (!option || !option.optionNumber || !attr || !attr.attributeName) {
      console.error('[userEditGuard] 고친 노출속성의 옵션·속성 이름을 못 찾아 기록하지 않음:', path)
      continue
    }
    const prev = edits.find(e => same(e, option.optionNumber, attr.attributeName))
    if (prev) prev.value = value
    else edits.push({ optionNumber: String(option.optionNumber), attributeName: attr.attributeName, value })
    changed = true
  }
  return { edits, changed }
}

/**
 * 견적 정리(원문 맞춤·겹침 정리)가 고르려는 바꿈 중 사람이 고친 칸은 빼서 따로 돌려준다.
 * @param {Array} changes - [{ optionNumber, attributeName, to, … }]
 * @param {Array|null} edits - quotationUserEdits
 * @returns {{ apply: Array, kept: Array }}
 */
export function keepUserEdits (changes, edits) {
  const list = Array.isArray(changes) ? changes : []
  if (!Array.isArray(edits) || !edits.length) return { apply: list, kept: [] }
  const apply = []
  const kept = []
  for (const c of list) (edits.some(e => same(e, c.optionNumber, c.attributeName)) ? kept : apply).push(c)
  return { apply, kept }
}

/**
 * AI 가 다시 쓴 한 옵션의 노출속성에 사람 값을 되돌려 놓는다(attrs 를 고친다).
 * @param {Array} attrs - exposedAttributes
 * @param {string} optionNumber
 * @param {Array|null} edits - quotationUserEdits
 * @returns {Array<{attributeName:string, from:*, to:*}>} 되돌린 것
 */
export function restoreUserEdits (attrs, optionNumber, edits) {
  if (!Array.isArray(attrs) || !Array.isArray(edits) || !edits.length) return []
  const back = []
  for (const a of attrs) {
    const e = a && edits.find(x => same(x, optionNumber, a.attributeName))
    if (!e || a.attributeValue === e.value) continue
    back.push({ attributeName: a.attributeName, from: a.attributeValue, to: e.value })
    a.attributeValue = e.value
  }
  return back
}

/** 옵션명(korName) 기록의 속성 이름 자리 — 노출속성 이름과 겹치지 않는 표식 */
export const KOR_NAME_FIELD = '@korName'

/**
 * SEO 화면 손 저장의 옵션명을 사람 값으로 적는다(옵션마다 하나 · 빈 이름은 안 적음 · 노출속성 기록은 그대로).
 * @param {Object} product - quotationUserEdits
 * @param {Array<{optionNumber:string, korName:string}>} seoOptions
 * @returns {Array} 새 quotationUserEdits
 */
export function recordKorNames (product, seoOptions) {
  const edits = (Array.isArray(product && product.quotationUserEdits) ? product.quotationUserEdits : []).map(e => ({ ...e }))
  for (const o of Array.isArray(seoOptions) ? seoOptions : []) {
    if (!o || !o.optionNumber || typeof o.korName !== 'string' || !o.korName.trim()) continue
    const prev = edits.find(e => same(e, o.optionNumber, KOR_NAME_FIELD))
    if (prev) prev.value = o.korName
    else edits.push({ optionNumber: String(o.optionNumber), attributeName: KOR_NAME_FIELD, value: o.korName })
  }
  return edits
}

/**
 * SEO 정리(fixOptionTerms 등)가 바꾼 옵션명 중, 바꾸기 전이 사람이 적은 그대로였던 것은 되돌린다.
 * @param {Array} before - 정리 전 [{optionNumber, korName}]
 * @param {Array} after - 정리 뒤
 * @param {Array|null} edits - quotationUserEdits
 * @returns {{ options: Array, kept: Array<{optionNumber:string, from:string, to:string}> }}
 */
export function keepUserKorNames (before, after, edits) {
  if (!Array.isArray(edits) || !edits.length || !Array.isArray(before) || !Array.isArray(after)) return { options: after, kept: [] }
  const kept = []
  const options = after.map(o => {
    const b = o && before.find(x => x && String(x.optionNumber) === String(o.optionNumber))
    const e = b && edits.find(x => same(x, o.optionNumber, KOR_NAME_FIELD))
    if (!e || b.korName !== e.value || o.korName === b.korName) return o
    kept.push({ optionNumber: String(o.optionNumber), from: b.korName, to: o.korName })
    return { ...o, korName: b.korName }
  })
  return { options, kept }
}

/**
 * AI 가 다시 쓴 옵션명에 사람이 SEO 화면에 저장한 옵션명을 되돌려 놓는다(새 배열 · saram 판 #737 ②).
 * keepUserKorNames 는 «정리 전 = 사람 값» 일 때만 되살려서, AI 가 다른 이름을 내면 사람 이름이 사라졌다.
 * @param {Array<{optionNumber:string, korName:string}>} options
 * @param {Array|null} edits - quotationUserEdits
 * @returns {{ options: Array, back: Array<{optionNumber:string, from:string, to:string}> }}
 */
export function restoreUserKorNames (options, edits) {
  if (!Array.isArray(options) || !Array.isArray(edits) || !edits.length) return { options, back: [] }
  const back = []
  const out = options.map(o => {
    const e = o && edits.find(x => same(x, o.optionNumber, KOR_NAME_FIELD))
    if (!e || o.korName === e.value) return o
    back.push({ optionNumber: String(o.optionNumber), from: o.korName, to: e.value })
    return { ...o, korName: e.value }
  })
  return { options: out, back }
}

/** 사람이 SEO 화면에 저장한 그 옵션의 옵션명(없으면 null) */
export function userKorNameOf (edits, optionNumber) {
  const e = Array.isArray(edits) && edits.find(x => same(x, optionNumber, KOR_NAME_FIELD))
  return e ? e.value : null
}

/** 이 옵션명이 사람이 저장한 그대로인가 — 사실 검사가 바꾸지 않을 옵션 */
export function isUserKorName (edits, optionNumber, korName) {
  return Array.isArray(edits) && edits.some(x => same(x, optionNumber, KOR_NAME_FIELD) && x.value === korName)
}

/** 견적서 노출속성 기록 수(SEO 옵션명 @korName 은 빼고) — «AI 로 전부 다시» 확인 창 숫자 */
export function quotationEditCount (edits) {
  return (Array.isArray(edits) ? edits : []).filter(e => e && e.attributeName !== KOR_NAME_FIELD).length
}

/**
 * 견적서 «AI 로 전부 다시» — 노출속성 기록만 비운다(SEO 옵션명 @korName 기록은 남김 · 총괄 조건 1 2차).
 * 도는 동안만 메모리에서 빼고, completed 로 끝난 뒤에만 저장소에 쓴다.
 * 실패(던짐·completed 아님)면 저장 0 · 메모리 기록 되돌림 — 사람 값을 잃지 않는다(총괄 조건 1).
 * @param {Object} product - quotationUserEdits 를 가진 상품(메모리)
 * @param {() => Promise<{status:string}>} run - 견적 다시 만들기
 * @param {(edits: Array) => Promise<void>} save - 저장소에 기록 쓰기
 * @returns {Promise<Object>} run 의 결과
 */
export async function runDiscardingUserEdits (product, run, save) {
  const before = product.quotationUserEdits
  const rest = (Array.isArray(before) ? before : []).filter(e => e && e.attributeName === KOR_NAME_FIELD)
  product.quotationUserEdits = rest
  let result
  try {
    result = await run()
  } catch (e) {
    product.quotationUserEdits = before
    throw e
  }
  if (!result || result.status !== 'completed') {
    product.quotationUserEdits = before
    return result
  }
  await save(rest)
  return result
}

export default { mergeUserEdits, keepUserEdits, restoreUserEdits, runDiscardingUserEdits, recordKorNames, keepUserKorNames, restoreUserKorNames, isUserKorName, userKorNameOf, quotationEditCount, KOR_NAME_FIELD }
